import { useEffect, useMemo, useState } from 'react';
import { Grid, Stack, Box, Paper, Typography } from '@mui/material';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import { useSelector } from 'react-redux';
import moment from 'moment/moment';
import Text from '../detail/components/Text.component';
import { fetchAPI } from '../../api/request';
import { API } from '../../module/mexal';
import _ from 'lodash';
import { tpNazionalitaOptions, codPaeseOptions } from '../../module/agency/conf/SendEmailForm.conf';

const hasFieldValue = (fieldValue) => {
  if (fieldValue === null || fieldValue === undefined) return false;
  return String(fieldValue).trim() !== '';
};

const mexalHeaderFields = [
  { key: 'invoice_entity', label: 'Invoice Entity' },
  { key: 'mexal_codice', label: 'Mexal Code' },
];

const mexalAddressFields = [
  { key: 'mexal_tp_nazionalita', label: 'Nationality' },
  { key: 'mexal_cod_paese', label: 'Country' },
  { key: 'mexal_indirizzo', label: 'Address' },
  { key: 'mexal_cap', label: 'ZIP Code' },
  { key: 'mexal_localita', label: 'City' },
  { key: 'mexal_provincia', label: 'Province' },
];

const mexalFieldsMap = {
  COMPANY: [
    { key: 'mexal_ragione_sociale', label: 'Company Name' },
    { key: 'mexal_partita_iva', label: 'VAT Number' },
    { key: 'mexal_pec', label: 'PEC' },
    { key: 'mexal_codice_sdi', label: 'SDI Code' },
  ],
  'NATURAL PERSON': [
    { key: 'mexal_nome', label: 'First Name' },
    { key: 'mexal_cognome', label: 'Last Name' },
    { key: 'mexal_codice_fiscale', label: 'Tax Code' },
  ],
  'SOLE TRADER': [
    { key: 'mexal_nome', label: 'First Name' },
    { key: 'mexal_cognome', label: 'Last Name' },
    { key: 'mexal_codice_fiscale', label: 'Tax Code' },
    { key: 'mexal_partita_iva', label: 'VAT Number' },
    { key: 'mexal_ragione_sociale', label: 'Company Name' },
    { key: 'mexal_denominazione', label: 'Business Name' },
    { key: 'mexal_pec', label: 'PEC' },
    { key: 'mexal_codice_sdi', label: 'SDI Code' },
  ],
};

const MexalDetails = ({ mexalData }) => {
  if (!mexalData) return null;

  const entityType = mexalData.invoice_entity;
  const specificFields = mexalFieldsMap[entityType] ?? [
    ...mexalFieldsMap.COMPANY,
    ...mexalFieldsMap['NATURAL PERSON'],
    ...mexalFieldsMap['SOLE TRADER'],
  ];
  const fieldsToRender = [...mexalHeaderFields, ...specificFields, ...mexalAddressFields];

  return (
    <Stack spacing={1}>
      {fieldsToRender.map(({ key, label }) =>
        hasFieldValue(mexalData[key]) ? <Text key={key} label={label} value={mexalData[key]} /> : null
      )}
    </Stack>
  );
};

const InvoicingDetails = ({ value }) => {
  if (value?.invoicing_entity === 'COMPANY') {
    return (
      <Stack spacing={1}>
        <Grid container>
          <Grid item xs={12} sm={12}>
            <Text label='Invoice Entity' value={value?.invoicing_entity} />
          </Grid>
          <Grid item xs={12}>
            <Text label='Company name' value={value?.invoice_company_name} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Text label='Street address' value={value?.invoice_street_address} />
          </Grid>
          {value?.invoice_street_address2 && (
            <Grid item xs={12} sm={6}>
              <Text label='Street address 2' value={value.invoice_street_address2} />
            </Grid>
          )}

          <Grid item xs={12} sm={12}>
            <Text label='VAT number' value={value?.vat_number_company_invoicing} />
          </Grid>
        </Grid>

        <Grid container>
          {value?.invoice_person_of_payment && (
            <Grid item xs={12} sm={6}>
              <Text
                label='Person in charge of payment'
                value={value?.invoice_person_of_payment}
              />
            </Grid>
          )}
          {value?.invoice_position && (
            <Grid item xs={12} sm={6}>
              <Text label='Position' value={value?.invoice_position} />
            </Grid>
          )}

          {value?.invoice_email && (
            <Grid item xs={12} sm={6}>
              <Text label='Email' value={value?.invoice_email} />
            </Grid>
          )}
          {value?.invoice_phone_number && (
            <Grid item xs={12} sm={6}>
              <Text label='Phone number' value={value.invoice_phone_number} />
            </Grid>
          )}
        </Grid>
      </Stack>
    );
  }

  if (value?.invoicing_entity === 'NATURAL PERSON') {
    return (
      <Stack spacing={1}>
        <Grid container>
          <Grid item xs={12} sm={12}>
            <Text label='Invoice Entity' value={value?.invoicing_entity} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Text label='First Name' value={value?.invoice_person_name} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Text label='Last Name' value={value?.invoice_person_lastname} />
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12} sm={6}>
            <Text
              label='Place of birth'
              value={value?.invoice_person_place_of_birth}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Text
              label='Birth date'
              value={moment(value?.invoice_person_birthdate).format('DD-MM-YYYY')}
            />
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12} sm={6}>
            <Text label='Street Address' value={value?.invoice_street_address} />
          </Grid>
          {value?.invoice_street_address2 && (
            <Grid item xs={12} sm={6}>
              <Text label='Street Address 2' value={value.invoice_street_address2} />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Text label='City' value={value?.invoice_city_address} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Text label='Country' value={value?.invoice_country_address} />
          </Grid>
          <Grid item xs={12} sm={12}>
            <Text label='Postal / Zip Code' value={value?.invoice_zipcode_addess} />
          </Grid>
        </Grid>
      </Stack>
    );
  }

  return <Text label='Invoice Entity' value='not set' />;
};

const YachtInvoicingDetails = ({
  value,
  showInvoicingDetails = true,
  showMexalTitle = true,
}) => {
  const { jwt } = useSelector((state) => state.login);
  const [mexalData, setMexalData] = useState(null);

  useEffect(() => {
    if (!value?.mexal_codice || !jwt) {
      setMexalData(null);
      return;
    }

    fetchAPI(API.mexalCustomerByMexalID, { id: value.mexal_codice }, '', {}, jwt)
      .then((response) => {
        const customer = response?.data?.attributes || response?.data || response;

        if (!customer) {
          setMexalData(null);
          return;
        }

        setMexalData({
          invoice_entity: customer.invoice_entity || '',
          mexal_codice: customer.mexal_code || value.mexal_codice,
          mexal_ragione_sociale: customer.mexal_ragione_sociale || '',
          mexal_nome: customer.mexal_nome || '',
          mexal_cognome: customer.mexal_cognome || '',
          mexal_indirizzo: customer.mexal_indirizzo || '',
          mexal_cap: customer.mexal_cap || '',
          mexal_localita: customer.mexal_localita || '',
          mexal_provincia: customer.mexal_provincia || '',
          mexal_tp_nazionalita: _.find(tpNazionalitaOptions, (o) => o.id === customer.mexal_tp_nazionalita)?.label || customer.mexal_tp_nazionalita || '',
          mexal_cod_paese: _.find(codPaeseOptions, (o) => o.id === customer.mexal_cod_paese)?.label || customer.mexal_cod_paese || '',
          mexal_partita_iva: customer.mexal_partita_iva || '',
          mexal_codice_fiscale: customer.mexal_codice_fiscale || '',
          mexal_pec: customer.mexal_pec || '',
          mexal_codice_sdi: customer.mexal_codice_sdi || '',
          mexal_denominazione: customer.mexal_denominazione || '',
        });
      })
      .catch(() => {
        setMexalData(null);
      });
  }, [jwt, value?.mexal_codice]);

  const mexalDisplayData = useMemo(() => {
    if (mexalData) return mexalData;
    if (hasFieldValue(value?.mexal_codice)) {
      return { mexal_codice: value?.mexal_codice };
    }
    return null;
  }, [mexalData, value?.mexal_codice]);

  const showMexalSection = useMemo(() => {
    if (!mexalDisplayData) return false;
    return Object.values(mexalDisplayData).some((fieldValue) => hasFieldValue(fieldValue));
  }, [mexalDisplayData]);

  return (
    <Grid container spacing={2}>
      {showInvoicingDetails && (
        <Grid item xs={12} md={showMexalSection ? 6 : 12}>
          <InvoicingDetails value={value} />
        </Grid>
      )}

      {showMexalSection && (
        <Grid item xs={12} md={showInvoicingDetails ? 6 : 12}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              backgroundColor: 'white',
              borderRadius: 4,
            }}
          >
            <Stack spacing={2}>
              {showMexalTitle && (
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                  }}
                >
                  <BusinessCenterIcon
                    sx={{ fontSize: 40, color: 'primary.main', mr: 1 }}
                  />
                  <Typography variant='h5' color='primary' fontWeight='bold'>
                    Mexal Invoicing Details
                  </Typography>
                </Box>
              )}
              <MexalDetails mexalData={mexalDisplayData} />
            </Stack>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default YachtInvoicingDetails;
